import type { PlatformUserRole, User } from "../services/authService";

function isActiveRoleStatus(status: string | null | undefined): boolean {
  if (!status?.trim()) return false;
  return status.trim().toUpperCase() === "ACTIVE";
}

/** Active role names from GET /platform/user-context roles array. */
export function normalizePlatformRoleNames(roles: PlatformUserRole[]): string[] {
  return roles
    .filter((role) => isActiveRoleStatus(role.roleStatus))
    .map((role) => role.roleName);
}

function normalizeRoleName(role: string | null | undefined): string {
  return role?.toLowerCase()?.trim() ?? "";
}

/** Collect role strings from a session user. */
export function getUserRoleNames(user: User | null | undefined): string[] {
  if (!user) return [];
  const fromArray = user.roles ?? [];
  const single = user.role ? [user.role] : [];
  return [...fromArray, ...single].filter(Boolean) as string[];
}

/** Admin-equivalent roles — used for project-manager eligibility, etc. */
export function isAdminRoleName(role: string | null | undefined): boolean {
  const normalized = normalizeRoleName(role);
  return (
    normalized === "admin" ||
    normalized === "system administrator"
  );
}

/** Platform catalog role that unlocks the Admin module. */
export function isSystemAdministratorRoleName(
  role: string | null | undefined,
): boolean {
  return normalizeRoleName(role) === "system administrator";
}

export function isProjectManagerRoleName(
  role: string | null | undefined,
): boolean {
  return normalizeRoleName(role) === "project manager";
}

/** Eligible to appear in the Project Manager dropdown. */
export function canActAsProjectManager(roleNames: string[]): boolean {
  return roleNames.some(
    (role) => isAdminRoleName(role) || isProjectManagerRoleName(role),
  );
}

/** True when the user has Admin (or System Administrator) or Project Manager. */
export function isAdminOrProjectManager(
  user: User | null | undefined,
): boolean {
  return canActAsProjectManager(getUserRoleNames(user));
}

/** True when the user may access the Admin module (System Administrator only). */
export function isAdminUser(user: User | null | undefined): boolean {
  return getUserRoleNames(user).some(isSystemAdministratorRoleName);
}
